import React from 'react';
import { Modal, Button, TextField, CircularProgress } from '@mui/material';
import { Close, Edit, ArrowForward } from '@mui/icons-material';

interface ZoneTrailer {
  puid: string;
  driver_id: string;
  driver_name: string;
  driver_phone_number?: string;
  truck_number?: string;
  trailer_number: string;
  direction: string;
  carrier_name?: string;
  zone_id: string;
  current_status?: string;
  trailer_uuid?: string;
  shipment_number?: string;
  active_lineage?: {
    uuid: string;
    notes?: string;
    created_at?: string;
  };
  data?: any;
}

interface ZoneTrailerDetailsModalProps {
  open: boolean;
  selectedZoneTrailer: ZoneTrailer | null;
  trailerNotesValue: string;
  isEditingTrailerNotes: boolean;
  savingTrailerNotes: boolean;
  isTrailerOOS: (trailer: ZoneTrailer) => boolean;
  formatDirection: (direction: string) => string;
  onClose: () => void;
  onMoveToYardSpot: (trailer: ZoneTrailer) => void;
  onMoveToDock: (trailer: ZoneTrailer) => void;
  onTrailerNotesChange: (value: string) => void;
  onEditTrailerNotes: (editing: boolean) => void;
  onSaveTrailerNotes: () => void;
}

const ZoneTrailerDetailsModal: React.FC<ZoneTrailerDetailsModalProps> = ({
  open,
  selectedZoneTrailer,
  trailerNotesValue,
  isEditingTrailerNotes,
  savingTrailerNotes,
  isTrailerOOS,
  formatDirection,
  onClose,
  onMoveToYardSpot,
  onMoveToDock,
  onTrailerNotesChange,
  onEditTrailerNotes,
  onSaveTrailerNotes,
}) => {
  if (!selectedZoneTrailer) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <div className="flex justify-center items-center w-full h-full">
        <div className="w-[500px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={onClose}
          />
          <h1 className="text-xl font-bold mb-5">Trailer {selectedZoneTrailer.trailer_number} Details</h1>
          
          {/* Trailer Information */}
          <div className="mb-4">
            <h2 className="font-bold mb-2">Trailer Information</h2>
            <div className="p-3 border rounded bg-gray-50">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="font-semibold">Trailer:</span> {selectedZoneTrailer.trailer_number}
                </div>
                {selectedZoneTrailer.carrier_name && (
                  <div>
                    <span className="font-semibold">SCAC:</span> {selectedZoneTrailer.carrier_name}
                  </div>
                )}
                {selectedZoneTrailer.current_status && (
                  <div>
                    <span className="font-semibold">Status:</span> {selectedZoneTrailer.current_status}
                  </div>
                )}
                {selectedZoneTrailer.direction && (
                  <div>
                    <span className="font-semibold">Direction:</span> {formatDirection(selectedZoneTrailer.direction)}
                  </div>
                )}
                {selectedZoneTrailer.shipment_number && (
                  <div className="col-span-2">
                    <span className="font-semibold">Shipment:</span> {selectedZoneTrailer.shipment_number}
                  </div>
                )}
                {selectedZoneTrailer.active_lineage?.created_at && (
                  <div className="col-span-2">
                    <span className="font-semibold">Trailer Check-In Date:</span> {new Date(selectedZoneTrailer.active_lineage.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mb-4">
            <div className="flex gap-2">
              <button 
                className="bg-green-500 hover:bg-green-600 text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1"
                onClick={() => {
                  onClose();
                  onMoveToYardSpot(selectedZoneTrailer);
                }}
              >
                <ArrowForward fontSize="small" className="mr-2" />
                <span>Move to Yard Spot</span>
              </button>
              <button 
                className={`${
                  isTrailerOOS(selectedZoneTrailer)
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-500 hover:bg-blue-600'
                } text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1`}
                disabled={isTrailerOOS(selectedZoneTrailer)}
                onClick={() => {
                  onClose();
                  onMoveToDock(selectedZoneTrailer);
                }}
                title={
                  isTrailerOOS(selectedZoneTrailer)
                    ? 'Trailer is out of service'
                    : 'Move to Dock'
                }
              >
                <ArrowForward fontSize="small" className="mr-2" />
                <span>Move to Dock</span>
              </button>
            </div>
          </div>

          {/* Trailer Notes Section */}
          {selectedZoneTrailer.trailer_uuid && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-bold">Trailer Notes</h2>
                {!isEditingTrailerNotes && (
                  <button
                    className="text-sky-600 text-sm hover:underline flex items-center"
                    onClick={() => onEditTrailerNotes(true)}
                  >
                    <Edit fontSize="small" className="mr-1" />
                    {selectedZoneTrailer.active_lineage?.notes ? 'Edit Trailer Notes' : 'Add Trailer Notes'}
                  </button>
                )}
              </div>
              
              {isEditingTrailerNotes ? (
                <div>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={trailerNotesValue}
                    onChange={(e) => onTrailerNotesChange(e.target.value)}
                    placeholder="Enter notes about this trailer..."
                    variant="outlined"
                    disabled={savingTrailerNotes}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button
                      size="small"
                      onClick={() => {
                        onTrailerNotesChange(selectedZoneTrailer.active_lineage?.notes || '');
                        onEditTrailerNotes(false);
                      }}
                      disabled={savingTrailerNotes}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={onSaveTrailerNotes}
                      disabled={savingTrailerNotes}
                      startIcon={savingTrailerNotes ? <CircularProgress size={16} /> : undefined}
                    >
                      {savingTrailerNotes ? 'Saving...' : 'Save Trailer Notes'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-3 border rounded bg-green-50">
                  {selectedZoneTrailer.active_lineage?.notes ? (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedZoneTrailer.active_lineage.notes}</p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No trailer notes added</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="mt-5 pt-3 border-t flex justify-end">
            <button
              className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ZoneTrailerDetailsModal;

